import React, { useEffect, useState, useCallback } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import RelatorioPesquisaDespesasResultado from "./RelatorioPesquisaDespesasResultado";
import "./RelatorioPesquisaDespesas.scss";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

function RelatorioPesquisaDespesas({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
  unidadeSelecionada = "",
}) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();

  // Dados para popular os filtros
  const [funcionarios, setFuncionarios] = useState([]);
  const [users, setUsers] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [caixas, setCaixas] = useState([]);

  // Filtros selecionados
  const [filtros, setFiltros] = useState({
    funcionarios: [],
    funcionarioUsers: [],
    fornecedores: [],
    clientes: [],
    categorias: [],
    caixas: [],
    metodosPagamento: [],
    despesaPaga: null, // null = todos, true = pagas, false = não pagas
    dataInicio: dataFiltro?.dataInicial || "",
    dataFim: dataFiltro?.dataFinal || "",
  });

  const [dadosRelatorio, setDadosRelatorio] = useState(null);

  const metodosPagamentoOpcoes = [
    { value: "Dinheiro", label: "Dinheiro" },
    { value: "Cheque", label: "Cheque" },
    { value: "Cheque Visado", label: "Cheque Visado" },
    { value: "Transferência de crédito", label: "Transferência de crédito" },
    { value: "Internet banking", label: "Internet banking" },
    { value: "Cartão do banco", label: "Cartão do banco" },
    {
      value: "Débito direto da conta bancária",
      label: "Débito direto da conta bancária",
    },
    { value: "Cartão de crédito", label: "Cartão de crédito" },
    { value: "Cartão de débito", label: "Cartão de débito" },
  ];

  // Fetch dados iniciais
  useEffect(() => {
    if (!dadosClinica?._id) return;

    const fetchDados = async () => {
      startLoading();
      try {
        const [funcRes, fornRes, cliRes, catRes, caixaRes, usersRes] =
          await Promise.all([
            sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/funcionarios/clinica/${dadosClinica._id}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            ),
            sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/fornecedores/clinica/despesa/${dadosClinica._id}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            ),
            sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/datagrid/${dadosClinica._id}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            ),
            sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/categoriasfinanceiro/clinica/despesa/${dadosClinica._id}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            ),
            sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${dadosClinica._id}?todas=true`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            ),
            sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/users/ativos/${dadosClinica._id}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            ),
          ]);

        const sortByField = (arr, field) =>
          [...arr].sort((a, b) =>
            (a[field] || "").localeCompare(b[field] || "", "pt"),
          );

        setFuncionarios(funcRes.funcionarios || []);
        setUsers(usersRes.users || []);
        setFornecedores(sortByField(fornRes.fornecedores || [], "nome"));
        setClientes(cliRes.clientes || []);
        setCategorias(
          sortByField(catRes.categoriasFinanceiro || [], "categoria"),
        );
        setCaixas(sortByField(caixaRes.caixas || [], "caixa"));
      } catch (err) {
        console.error("Erro ao carregar dados:", err);
        toast.error("Erro ao carregar dados dos filtros.");
      } finally {
        stopLoading();
      }
    };

    fetchDados();
  }, [dadosClinica?._id, auth.token, sendRequest]);

  // Sincroniza datas do filtro pai quando mudam
  useEffect(() => {
    setFiltros((prev) => ({
      ...prev,
      dataInicio: dataFiltro?.dataInicial || "",
      dataFim: dataFiltro?.dataFinal || "",
    }));
  }, [dataFiltro]);

  // Dispara busca quando atualizacoes muda (botão Gerar/Exportar clicado no pai)
  useEffect(() => {
    if (atualizacoes > 0) {
      handleGerar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atualizacoes]);

  const handleGerar = useCallback(async () => {
    if (!dadosClinica?._id) return;

    startLoading();
    try {
      const body = {
        clinicaId: dadosClinica._id,
        funcionarios: filtros.funcionarios,
        funcionarioUsers: filtros.funcionarioUsers,
        fornecedores: filtros.fornecedores,
        clientes: filtros.clientes,
        categorias: filtros.categorias,
        caixas: filtros.caixas,
        metodosPagamento: filtros.metodosPagamento,
        despesaPaga: filtros.despesaPaga,
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        // Vazio = consolidado da empresa.
        ...(unidadeSelecionada && { estabelecimento: unidadeSelecionada }),
      };

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/despesas/relatoriopesquisa`,
        "POST",
        JSON.stringify(body),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      setDadosRelatorio(responseData);
    } catch (err) {
      console.error("Erro ao gerar relatório:", err);
      toast.error("Erro ao gerar relatório de despesas.");
    } finally {
      stopLoading();
    }
  }, [dadosClinica?._id, filtros, auth.token, sendRequest]);

  // ── Helpers de renderização ──────────────────────────────────────────────

  const renderMultiSelect = (
    label,
    campo,
    opcoes,
    labelKey = "nome",
    valueKey = "_id",
  ) => (
    <FormControl size="small" style={{ minWidth: 220 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={filtros[campo]}
        onChange={(e) =>
          setFiltros((prev) => ({ ...prev, [campo]: e.target.value }))
        }
        input={<OutlinedInput label={label} />}
        renderValue={(selected) =>
          selected
            .map((id) => {
              const op = opcoes.find((o) => o[valueKey] === id);
              return op ? op[labelKey] : id;
            })
            .join(", ")
        }
      >
        {opcoes.map((op) => (
          <MenuItem key={op[valueKey]} value={op[valueKey]}>
            <Checkbox checked={filtros[campo].includes(op[valueKey])} />
            <ListItemText primary={op[labelKey]} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Lista combinada para o select — funcionários marcados com "funcionario", users com "user"
  const opcoesFuncionarios = [
    ...funcionarios.map((f) => ({
      _id: f._id,
      nome: f.nome,
      tipo: "funcionario",
    })),
    ...users.map((u) => ({ _id: u._id, nome: u.name, tipo: "user" })),
  ].sort((a, b) => a.nome.localeCompare(b.nome, "pt"));

  // IDs atualmente selecionados (para o value do Select)
  const funcionariosSelecionados = [
    ...filtros.funcionarios,
    ...filtros.funcionarioUsers,
  ];

  return (
    <div className="relatorio-despesas">
      <h3 className="relatorio-despesas__titulo">Relatório de Despesas</h3>

      <div className="relatorio-despesas__filtros">
        {/* Datas */}
        <div className="relatorio-despesas__filtros-row">
          <div className="relatorio-despesas__filtro-grupo">
            <label className="relatorio-despesas__label">
              Data Início (criação)
            </label>
            <input
              type="date"
              className="relatorio-despesas__input"
              value={filtros.dataInicio}
              onChange={(e) =>
                setFiltros((p) => ({ ...p, dataInicio: e.target.value }))
              }
            />
          </div>
          <div className="relatorio-despesas__filtro-grupo">
            <label className="relatorio-despesas__label">
              Data Fim (criação)
            </label>
            <input
              type="date"
              className="relatorio-despesas__input"
              value={filtros.dataFim}
              onChange={(e) =>
                setFiltros((p) => ({ ...p, dataFim: e.target.value }))
              }
            />
          </div>

          {/* Estado de pagamento */}
          <FormControl size="small" style={{ minWidth: 220 }}>
            <InputLabel>Estado de Pagamento</InputLabel>
            <Select
              value={
                filtros.despesaPaga === null ? "" : String(filtros.despesaPaga)
              }
              onChange={(e) => {
                const val = e.target.value;
                setFiltros((prev) => ({
                  ...prev,
                  despesaPaga: val === "" ? null : val === "true",
                }));
              }}
              input={<OutlinedInput label="Estado de Pagamento" />}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="true">Pagas</MenuItem>
              <MenuItem value="false">Não Pagas</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="relatorioPesquisaDespesa__row">
          {/* Funcionários + Utilizadores */}
          <FormControl size="small" style={{ minWidth: 220 }}>
            <InputLabel>Funcionários</InputLabel>
            <Select
              multiple
              value={funcionariosSelecionados}
              onChange={(e) => {
                const selected = e.target.value; // array de IDs
                const novosFuncionarios = selected.filter((id) =>
                  opcoesFuncionarios.find(
                    (op) => op._id === id && op.tipo === "funcionario",
                  ),
                );
                const novosUsers = selected.filter((id) =>
                  opcoesFuncionarios.find(
                    (op) => op._id === id && op.tipo === "user",
                  ),
                );
                setFiltros((prev) => ({
                  ...prev,
                  funcionarios: novosFuncionarios,
                  funcionarioUsers: novosUsers,
                }));
              }}
              input={<OutlinedInput label="Funcionários" />}
              renderValue={(selected) =>
                selected
                  .map((id) => {
                    const op = opcoesFuncionarios.find((o) => o._id === id);
                    return op ? op.nome : id;
                  })
                  .join(", ")
              }
            >
              {opcoesFuncionarios.map((op) => (
                <MenuItem key={op._id} value={op._id}>
                  <Checkbox
                    checked={funcionariosSelecionados.includes(op._id)}
                  />
                  <ListItemText
                    primary={op.nome}
                    secondary={
                      op.tipo === "user" ? "Utilizador" : "Funcionário"
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Fornecedores */}
          {renderMultiSelect("Fornecedores", "fornecedores", fornecedores)}

          {/* Clientes */}
          <FormControl size="small" style={{ minWidth: 220 }}>
            <Autocomplete
              size="small"
              options={clientes}
              getOptionLabel={(op) => op.nome || ""}
              value={
                clientes.find((c) => c._id === filtros.clientes[0]) || null
              }
              onChange={(_, newValue) =>
                setFiltros((prev) => ({
                  ...prev,
                  clientes: newValue ? [newValue._id] : [],
                }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Cliente" />
              )}
            />
          </FormControl>

          {/* Categorias */}
          {renderMultiSelect(
            "Categorias",
            "categorias",
            categorias,
            "categoria",
            "_id",
          )}

          {/* Caixas */}
          {renderMultiSelect("Caixas", "caixas", caixas, "caixa", "_id")}

          {/* Métodos de Pagamento */}
          {renderMultiSelect(
            "Métodos de Pagamento",
            "metodosPagamento",
            metodosPagamentoOpcoes,
            "label",
            "value",
          )}
        </div>
      </div>

      <div className="relatorio-despesas__acoes">
        <button className="relatorio-despesas__btn" onClick={handleGerar}>
          Gerar Relatório
        </button>
        <button
          className="relatorio-despesas__btn relatorio-despesas__btn--limpar"
          onClick={() =>
            setFiltros({
              funcionarios: [],
              fornecedores: [],
              funcionarioUsers: [],
              clientes: [],
              categorias: [],
              caixas: [],
              metodosPagamento: [],
              despesaPaga: null,
              dataInicio: "",
              dataFim: "",
            })
          }
        >
          Limpar Filtros
        </button>
      </div>

      {dadosRelatorio && (
        <RelatorioPesquisaDespesasResultado dados={dadosRelatorio} />
      )}
    </div>
  );
}

export default RelatorioPesquisaDespesas;
